import React from "react";
import { useCustomer } from "../../../contexts/api/customers";
import type { Excursion } from "../../../api/excursions/Excursion.typed";
import { DEFAULT_PAGINATION_LIMIT } from "../../../utils/paginationHelpers";

export function useCustomerExcursions(customerId: number | undefined) {
  const { getCustomerExcursions } = useCustomer();

  const [excursions, setExcursions] = React.useState<Excursion[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState(DEFAULT_PAGINATION_LIMIT);

  const fetchPage = React.useCallback(
    async (page: number, limit: number) => {
      if (!customerId) return;

      setLoading(true);

      try {
        const result = await getCustomerExcursions(customerId, page, limit);

        if (!result) {
          setExcursions([]);
          setTotalItems(0);
          setTotalPages(0);

          return;
        }

        setExcursions(result.data);
        setTotalItems(result.total);
        setTotalPages(result.total_pages);
        setCurrentPage(result.page);
      } finally {
        setLoading(false);
      }
    },
    [customerId, getCustomerExcursions],
  );

  React.useEffect(() => {
    fetchPage(1, itemsPerPage);
  }, [customerId, itemsPerPage, fetchPage]);

  const handlePageChange = React.useCallback(
    (page: number) => {
      fetchPage(page, itemsPerPage);
    },
    [fetchPage, itemsPerPage],
  );

  const handleItemsPerPageChange = React.useCallback(
    (limit: number) => {
      setItemsPerPage(limit);
      fetchPage(1, limit);
    },
    [fetchPage],
  );

  return {
    excursions,
    loading,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
  };
}
